import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, Input, Form, Alert } from "antd";
import { EyeOutlined, EyeInvisibleOutlined, MailOutlined, LockOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import OtpInput from "react-otp-input";

// API imports
import { forgot_password_API, set_password_API } from '@/utils/api';

// Store imports
import { setforgotPasswordShow } from '@/store/reducers/forgotPassword';
import { setOtpShow } from '@/store/reducers/otpValueShow';
import { setLoginShow } from '@/store/reducers/loginShow';
import { setsetpasswordShow } from '@/store/reducers/setpasswordshow';

// Component imports
import AnimateButton from '@/components/@extended/AnimateButton';
import { defaultColors } from '@/themes/antd-theme';

// ============================|| FORGOT PASSWORD FORM ||============================ //

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // State
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");

  // Redux state
  const forgotPasswordShow = useSelector((state) => state.forgotPasswordShow);
  const otpShow = useSelector((state) => state.otpShow);
  const newpasswordShow = useSelector((state) => state.setpasswordShow);

  // Back to login handler
  const handleBackToLogin = () => {
    dispatch(setLoginShow(true));
    dispatch(setforgotPasswordShow(false));
    dispatch(setOtpShow(false));
    dispatch(setsetpasswordShow(false));
  };

  // Step 1: Send email
  const onEmailSubmit = async (values) => {
    setLoading(true);
    setForgotEmail(values.email);

    await forgot_password_API(values.email, (res) => {
      setLoading(false);
      if (res.status === "success") {
        toast.success(res.message);
        dispatch(setforgotPasswordShow(true));
        dispatch(setOtpShow(true));
        dispatch(setsetpasswordShow(false));
        dispatch(setLoginShow(false));
      } else if (res.status === "failed") {
        toast.warning(res.message);
      }
    });
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (otpValue.length === 6) {
      dispatch(setsetpasswordShow(true));
      dispatch(setOtpShow(false));
      setOtpError("");
    } else {
      setOtpError("Please enter a valid 6-digit OTP");
    }
  };

  // Step 3: Set new password
  const onPasswordSubmit = async (values) => {
    setLoading(true);
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

    if (!passwordRegex.test(values.newPassword)) {
      form.setFields([{
        name: 'newPassword',
        errors: ['Password must contain at least 8 characters, one letter, one number, and one special character']
      }]);
      setLoading(false);
      return;
    }

    await set_password_API(
      {
        email: forgotEmail,
        otp: otpValue,
        new_password: values.newPassword,
        confirm_password: values.confirmPassword,
      },
      (res) => {
        setLoading(false);
        if (res.status === "success") {
          toast.success(res.message);
          handleBackToLogin();
        } else if (res.status === "failed") {
          toast.warning(res.message);
        }
      }
    );
  };

  // Step 1: Email form
  if (forgotPasswordShow && !otpShow && !newpasswordShow) {
    return (
      <Form form={form} layout="vertical" onFinish={onEmailSubmit} className="w-full">
        <Form.Item
          label={<span className="text-sm font-medium">Email Address</span>}
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-gray-400" />}
            placeholder="Enter email address"
            size="large"
            className="rounded-md"
          />
        </Form.Item>

        <div className="flex justify-end mb-4 -mt-2">
          <RouterLink
            to=""
            onClick={(e) => { e.preventDefault(); handleBackToLogin(); }}
            className="text-sm no-underline hover:underline"
            style={{ color: defaultColors.primary }}
          >
            Back to Login
          </RouterLink>
        </div>

        <Form.Item className="mb-0">
          <AnimateButton>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full font-semibold"
              style={{
                backgroundColor: defaultColors.primary,
                borderColor: defaultColors.primary,
              }}
            >
              Send Email
            </Button>
          </AnimateButton>
        </Form.Item>
      </Form>
    );
  }

  // Step 2: OTP verification
  if (forgotPasswordShow && otpShow && !newpasswordShow) {
    return (
      <form onSubmit={handleOtpSubmit} className="w-full">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <OtpInput
              value={otpValue}
              onChange={setOtpValue}
              inputType="tel"
              numInputs={6}
              renderSeparator={<span className="w-3" />}
              renderInput={(props) => <input {...props} />}
              shouldAutoFocus
              inputStyle={{
                height: "50px",
                width: "50px",
                borderRadius: "8px",
                border: `2px solid ${defaultColors.palette.gray[300]}`,
                fontSize: "24px",
                color: defaultColors.primary,
                textAlign: "center",
              }}
            />
          </div>

          {otpError && (
            <Alert message={otpError} type="error" showIcon className="mt-2" />
          )}

          <div className="flex justify-end -mt-2">
            <RouterLink
              to=""
              onClick={(e) => { e.preventDefault(); handleBackToLogin(); }}
              className="text-sm no-underline hover:underline"
              style={{ color: defaultColors.primary }}
            >
              Back to Login
            </RouterLink>
          </div>

          <AnimateButton>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="w-full font-semibold"
              style={{
                backgroundColor: defaultColors.primary,
                borderColor: defaultColors.primary,
              }}
            >
              Verify OTP
            </Button>
          </AnimateButton>
        </div>
      </form>
    );
  }

  // Step 3: Set new password
  if (forgotPasswordShow && !otpShow && newpasswordShow) {
    return (
      <Form form={form} layout="vertical" onFinish={onPasswordSubmit} className="w-full">
        <Form.Item
          label={<span className="text-sm font-medium">New Password</span>}
          name="newPassword"
          rules={[{ required: true, message: 'Please enter new password' }]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Enter new password"
            size="large"
            className="rounded-md"
            iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          />
        </Form.Item>

        <Form.Item
          label={<span className="text-sm font-medium">Confirm Password</span>}
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Confirm new password"
            size="large"
            className="rounded-md"
            iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          />
        </Form.Item>

        <div className="flex justify-end mb-4 -mt-2">
          <RouterLink
            to=""
            onClick={(e) => { e.preventDefault(); handleBackToLogin(); }}
            className="text-sm no-underline hover:underline"
            style={{ color: defaultColors.primary }}
          >
            Back to Login
          </RouterLink>
        </div>

        <Form.Item className="mb-0">
          <AnimateButton>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full font-semibold"
              style={{
                backgroundColor: defaultColors.primary,
                borderColor: defaultColors.primary,
              }}
            >
              Reset Password
            </Button>
          </AnimateButton>
        </Form.Item>
      </Form>
    );
  }

  return null;
};

export default ForgotPassword;
